import { APP } from '../tokens';

const F = APP.fontFamily;

// ─── Icons ──────────────────────────────────────

function ShieldIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={APP.textReviewAccepted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#FDFCFB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function PenIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
  );
}

function ExportIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#9E9189" strokeWidth="2" style={{ flexShrink: 0, marginLeft: 'auto' }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ─── Props ──────────────────────────────────────

interface ReviewCompleteBlockProps {
  onSaveRevised: () => void;
  /** Register revised Word doc as a new project/workspace version and return to doc view */
  onApplyToWorkspace?: () => void | Promise<void>;
  showApplyToWorkspace?: boolean;
  applyToWorkspaceDisabled?: boolean;
  applyToWorkspaceBusy?: boolean;
  onSendForSignature: () => void;
  onExportToGmail: () => void;
  onViewOriginal: () => void;
}

// ─── Component ──────────────────────────────────

export default function ReviewCompleteBlock({
  onSaveRevised,
  onApplyToWorkspace,
  showApplyToWorkspace,
  applyToWorkspaceDisabled,
  applyToWorkspaceBusy,
  onSendForSignature,
  onExportToGmail,
  onViewOriginal,
}: ReviewCompleteBlockProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        paddingBlock: 12,
        paddingInline: 16,
        gap: 10,
        borderTop: `1px solid ${APP.borderReviewCardDefault}`,
      }}
    >
      {/* Snapshot info */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: 5,
          paddingBlock: 6,
          paddingInline: 8,
          gap: 6,
          backgroundColor: APP.bgReviewCardAccepted,
        }}
      >
        <ShieldIcon />
        <span style={{ fontSize: 10, fontWeight: 400, color: APP.textReviewAccepted, lineHeight: '12px', fontFamily: F }}>
          Pre-revision snapshot saved · {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })},{' '}
          {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
        </span>
      </div>

      {showApplyToWorkspace && onApplyToWorkspace ? (
        <button
          type="button"
          className="btn-hover"
          disabled={applyToWorkspaceBusy || applyToWorkspaceDisabled}
          onClick={() => { void onApplyToWorkspace(); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 6,
            paddingBlock: 9,
            gap: 6,
            backgroundColor: applyToWorkspaceBusy || applyToWorkspaceDisabled ? '#E8DED4' : '#D97645',
            border: 'none',
            cursor: applyToWorkspaceBusy || applyToWorkspaceDisabled ? 'default' : 'pointer',
            fontFamily: F,
            fontSize: 12,
            fontWeight: 600,
            color: '#FDFCFB',
            lineHeight: '16px',
          }}
        >
          <DownloadIcon />
          {applyToWorkspaceBusy ? 'Applying…' : 'Apply accepted suggestions to document'}
        </button>
      ) : null}

      <button
        type="button"
        className="btn-hover"
        onClick={onSaveRevised}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 6,
          paddingBlock: 9,
          gap: 6,
          backgroundColor: APP.textDark,
          border: 'none',
          cursor: 'pointer',
          fontFamily: F,
          fontSize: 12,
          fontWeight: 500,
          color: '#FDFCFB',
          lineHeight: '16px',
        }}
      >
        <DownloadIcon />
        Download revised Word document
      </button>

      {/* Next Steps */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span
          style={{
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.06em',
            color: APP.textReviewMuted,
            lineHeight: '12px',
            fontFamily: F,
            textTransform: 'uppercase',
          }}
        >
          Next Steps
        </span>

        {/* Send for signature */}
        <button
          type="button"
          className="btn-hover"
          onClick={onSendForSignature}
          style={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: 6,
            paddingBlock: 7,
            paddingInline: 10,
            gap: 6,
            backgroundColor: '#FDFCFB',
            border: `1px solid ${APP.borderReviewCardDefault}`,
            cursor: 'pointer',
            fontFamily: F,
            fontSize: 11,
            fontWeight: 400,
            color: APP.textDark,
            lineHeight: '14px',
          }}
        >
          <PenIcon />
          Send for signature
          <ChevronRight />
        </button>

        {/* Export to Gmail */}
        <button
          type="button"
          className="btn-hover"
          onClick={onExportToGmail}
          style={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: 6,
            paddingBlock: 7,
            paddingInline: 10,
            gap: 6,
            backgroundColor: '#FDFCFB',
            border: `1px solid ${APP.borderReviewCardDefault}`,
            cursor: 'pointer',
            fontFamily: F,
            fontSize: 11,
            fontWeight: 400,
            color: APP.textDark,
            lineHeight: '14px',
          }}
        >
          <ExportIcon />
          Export to Gmail
          <ChevronRight />
        </button>
      </div>

      {/* View original version */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <button
          type="button"
          className="btn-hover"
          onClick={onViewOriginal}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontSize: 10,
            fontWeight: 400,
            color: APP.textReviewMuted,
            lineHeight: '12px',
            fontFamily: F,
          }}
        >
          View original version
        </button>
      </div>
    </div>
  );
}
